/* eslint-disable no-extra-boolean-cast */
/* eslint-disable react-native/no-inline-styles */
/**
 * Item Component
 * 
 * Displays menu item with image, price, description, and add-to-cart controls
 * Features:
 * - Item type indicator (veg/non-veg icon)
 * - Optional item image
 * - Favorite toggle (heart icon)
 * - Add to cart quantity controls
 * - Out of stock indicator
 * - Employee-specific info display
 * - Customizable display based on canteen settings
 * 
 * @component
 * @param {Object} item - Menu item object
 * @param {Object} canteen - Canteen object with settings
 * @param {boolean} [isCartItem] - Whether displayed in cart context
 * @param {boolean} [disableFav] - Disable favorite functionality
 * @param {Function} [onSlotSelect] - Callback when slot selected
 */
import React, { useMemo, useState } from 'react';
import { Image, StyleSheet, View } from 'react-native';
import { Chip, Icon, List, Text } from 'react-native-paper';
import { theme } from '../../theme/theme';
import { useSelector } from 'react-redux';
import ItemCart from './ItemCart';
import { getItemTypeIconColor } from '../../services/Utils';
// import ItemModal from './ItemModal';

function Item({ item, canteen, isCartItem, disableFav, onSlotSelect }) {
    const info = useSelector(state => state.info?.info);
    const customer = useSelector((state) => state.customer?.customer);
    const [isFav, setIsFav] = useState(item?.fav_id);
    const hideFav = isCartItem || disableFav;
    const settings = canteen?.settings || {};
    const assets = useSelector(state => state.info.info.assets);

    /**
     * Renders add-to-cart controls with optional image
     * Layout varies based on: isCartItem, enable_item_images, in_stock status
     * @returns {JSX.Element} Cart controls or out-of-stock message
     */
    const renderAddCartBelowImage = () => {
        const OutOfStock = <Text style={{ color: theme.colors.error, textTransform: 'uppercase', fontSize: 12 }}>out of stock</Text>;

        // Case 1: Item already in cart
        if (isCartItem) {
            return (
                <View style={{ justifyContent: 'flex-start' }}>
                    <View style={[styles.cartItemBtnsConatiner, { borderColor: theme.colors.primary }]}>
                        <ItemCart onSlotSelect={onSlotSelect} canteen={canteen} item={item} isCartItem />
                    </View>
                </View>
            );
        }

        // Case 2: Images enabled
        if (canteen?.enable_item_images) {
            return item?.in_stock ? (
                <View style={styles.cartContainer}>
                    <Image
                        source={item?.image ? { uri: item.image } : require('../../assets/default.jpg')}
                        style={styles.img}
                    />
                    <View style={[styles.cartBtnsConatiner, { borderColor: theme.colors.primary }]}>
                        <ItemCart onSlotSelect={onSlotSelect} canteen={canteen} item={item} />
                    </View>
                </View>
            ) : (
                OutOfStock
            );
        }

        // Case 3: No images
        return item?.in_stock ? (
            <View style={[styles.cartBtnsConatinerWithoutImage, { borderColor: theme.colors.primary }]}>
                <ItemCart onSlotSelect={onSlotSelect} canteen={canteen} item={item} />
            </View>
        ) : (
            OutOfStock
        );
    };

    /**
     * Renders employee-specific counter info if configured
     * Shows only for specific employee types defined in settings
     * @returns {JSX.Element|null} Info text or null
     */
    const renderInfo = () => {
        if (!!settings?.show_emp_cntr_info && !item?.item_settings?.hide_emp_cntr_info) {
            if (!!settings?.cntr_employee_types) {
                const cntr_employee_types = (settings?.cntr_employee_types).split(',').map(Number);
                if (cntr_employee_types.includes(customer?.employee_type_id)) {
                    return <Text style={{ marginTop: 4 }} numberOfLines={2}>{settings?.show_emp_cntr_info}</Text>;
                }
            }
        }
        return null;
    };

    /**
     * Renders item price, favorite button, and description
     * @returns {JSX.Element} Item details section
     */
    const renderDescription = () => {
        return (
            <View style={{ justifyContent: 'flex-start' }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 4 }}>
                    {!!canteen?.item_price_shown && <Text style={isCartItem ? { marginLeft: 22 } : {}}>{info?.currency_symbol}{item?.item_price}</Text>}
                    {!hideFav && <Chip mode="outlined" compact={true} style={{ marginHorizontal: 6, borderWidth: 0 }} textStyle={{ fontSize: 12 }} icon={isFav ? 'heart' : 'heart-outline'} onPress={() => setIsFav((prev) => !prev)}>Save to Favs</Chip>}
                </View>
                {(!isCartItem && !!item?.item_description) && <Text style={{ marginTop: 4 }} numberOfLines={null}>{item?.item_description}</Text>}
                {renderInfo()}
            </View>
        );
    };

    const assetMap = useMemo(
        () =>
            Object.fromEntries(
                (assets || []).map(a => [a.asset_name?.toLowerCase(), a.asset_url]),
            ),

        [assets],
    );
    const assetUrl = assetMap[item?.item_type?.toLowerCase()];

    const renderTitle = () => {
        return (
            <View style={{ justifyContent: 'flex-start' }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 4 }}>
                    {assetUrl ? (
                        <Image
                            source={{ uri: assetUrl }}
                            style={styles.assetIcon}
                            resizeMode="contain"
                        />
                    ) : (
                        <Icon
                            source="square-circle"
                            size={16}
                            color={getItemTypeIconColor(item?.item_type)}
                        />
                    )}
                    <Text variant="titleSmall" style={{ marginHorizontal: 6, textTransform: 'capitalize' }}>{item?.item_name}</Text>
                </View>
            </View>
        );
    };

    return (
        <React.Fragment>
            <List.Item
                style={{ backgroundColor: '#ffffff' }}
                title={renderTitle}
                description={renderDescription}
                descriptionEllipsizeMode="tail"
                titleNumberOfLines={2}
                right={props => renderAddCartBelowImage()}
            />
        </React.Fragment>
    );
}

const styles = StyleSheet.create({
    cartContainer: {
        borderRadius: 8,
        position: 'relative',
        height: 98,
        shadowColor: '#000',
        shadowOffset: {
            width: 0,
            height: 2,
        },
        shadowOpacity: 0.23,
        shadowRadius: 2.62,

        elevation: 4,
    },
    cartBtnsConatiner: {
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'space-between',
        backgroundColor: '#ffffff',
        borderRadius: 8,
        borderWidth: 1,
        position: 'absolute',
        alignSelf: 'center',
        bottom: -10,
        maxHeight: 32,
        marginHorizontal: 8,
    },
    cartItemBtnsConatiner: {
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'space-between',
        backgroundColor: '#ffffff',
        borderRadius: 8,
        borderWidth: 1,
        maxHeight: 32,
    },
    cartBtnsConatinerWithoutImage: {
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'space-between',
        backgroundColor: '#ffffff',
        borderRadius: 8,
        borderWidth: 1,
        maxHeight: 32,
        marginHorizontal: 4,
    },
    img: {
        borderRadius: 8,
        width: 116,
        height: 98,
        backgroundColor: '#cccccc',
    },
    assetIcon: {
        width: 18,
        height: 18,
        marginRight: 4,
    },
});

export default Item;
